import { useEffect } from 'react';
import { $getNodeByKey, $getSelection, $isElementNode, $isRangeSelection, COMMAND_PRIORITY_EDITOR, LexicalNode } from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    APPLY_RAW_HTML_TABLE_PRESET_COMMAND,
    PATCH_RAW_HTML_TABLE_CELL_STYLE_COMMAND,
    PATCH_RAW_HTML_TABLE_STYLE_COMMAND,
    RawHtmlStylePatch,
} from './commands';
import { RawHtmlTableCellStyle, RawHtmlTableStyle, RawHtmlTableStylePresets } from './tableStyle';
import { $isRawHtmlTableNode, RawHtmlTableNode } from './RawHtmlTableNode';
import { $isRawHtmlTableCellNode, RawHtmlTableCellNode } from './RawHtmlTableCellNode';
import { $getRawHtmlTableCellsFromSelection, $getRawHtmlTableFromSelection } from './selection';

function mergeStyle<T extends object>(current: T | null, patch: RawHtmlStylePatch<T>): T | null
{
    const result: any = { ...(current ?? {}) };
    for (const [key, value] of Object.entries(patch))
    {
        if (value === null || value === '') { delete result[key]; }
        else { result[key] = value; }
    }
    return Object.keys(result).length > 0 ? result as T : null;
}

function $getRawHtmlTableByKey(tableKey?: string): RawHtmlTableNode | null
{
    if (!tableKey) { return null; }
    const node = $getNodeByKey(tableKey);
    return $isRawHtmlTableNode(node) ? node : null;
}

function $getRawHtmlTableCellsFromTable(table: RawHtmlTableNode): RawHtmlTableCellNode[]
{
    const cells: RawHtmlTableCellNode[] = [];

    for (const row of table.getChildren())
    {
        if (!$isElementNode(row)) { continue; }

        for (const cell of row.getChildren())
        {
            if ($isRawHtmlTableCellNode(cell)) { cells.push(cell); }
        }
    }

    return cells;
}

export default function RawHtmlTableStylePlugin()
{
    const [editor] = useLexicalComposerContext();

    useEffect(() =>
    {
        return mergeRegister
        (
            editor.registerCommand
            (
                PATCH_RAW_HTML_TABLE_STYLE_COMMAND,
                payload =>
                {
                    const table = $getRawHtmlTableByKey(payload.tableKey)
                        ?? $getRawHtmlTableFromSelection();

                    if (!table) { return false; }

                    table.setRawHtmlTableStyle
                    (
                        mergeStyle<RawHtmlTableStyle>
                        (
                            table.getRawHtmlTableStyle() ?? null,
                            payload.patch
                        )
                    );

                    return true;
                },
                COMMAND_PRIORITY_EDITOR
            ),

            editor.registerCommand
            (
                PATCH_RAW_HTML_TABLE_CELL_STYLE_COMMAND,
                payload =>
                {
                    let cells: RawHtmlTableCellNode[] = [];

                    if (payload.cellKeys && payload.cellKeys.length > 0)
                    {
                        cells = payload.cellKeys
                            .map(key => $getNodeByKey(key))
                            .filter($isRawHtmlTableCellNode);
                    }

                    if (cells.length === 0 && payload.tableKey)
                    {
                        const table = $getRawHtmlTableByKey(payload.tableKey);
                        if (table) { cells = $getRawHtmlTableCellsFromTable(table); }
                    }

                    if (cells.length === 0) { cells = $getRawHtmlTableCellsFromSelection(); }

                    if (cells.length === 0) { return false; }

                    for (const cell of cells)
                    {
                        cell.setRawHtmlTableCellStyle
                        (
                            mergeStyle<RawHtmlTableCellStyle>
                            (
                                cell.getRawHtmlTableCellStyle() ?? null,
                                payload.patch
                            )
                        );
                    }

                    return true;
                },
                COMMAND_PRIORITY_EDITOR
            ),

            editor.registerCommand
            (
                APPLY_RAW_HTML_TABLE_PRESET_COMMAND,
                payload =>
                {
                    const table = $getRawHtmlTableByKey(payload.tableKey)
                        ?? $getRawHtmlTableFromSelection();

                    if (!table) { return false; }

                    const preset = RawHtmlTableStylePresets[payload.presetName];

                    table.setRawHtmlTableStyle(preset.table ?? null);

                    for (const cell of $getRawHtmlTableCellsFromTable(table))
                    {
                        cell.setRawHtmlTableCellStyle(preset.cell ?? null);
                    }

                    return true;
                },
                COMMAND_PRIORITY_EDITOR
            )
        );
    }, [editor]);

    return null;
}